import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../api/client.js'

export interface Recording {
  id: string
  callId: string
  storageKey: string
  sizeBytes: number
  durationSec: number
  kekVersion: number
  pciPauseSegments: Array<{ startOffsetMs: number; endOffsetMs: number }>
  createdAt: string
  // Joined / derived fields populated by the Task-0a-enriched backend response:
  agentId?: string
  agentName?: string
  queueId?: string
  queueName?: string
  hasTranscript?: boolean
}

export interface RecordingFilters {
  fromDate: Date | null
  toDate: Date | null
  agentId: string | null
  queueId: string | null
  durationMinSec: number | null
  durationMaxSec: number | null
  hasTranscript: boolean | null
}

/**
 * Fetches paginated recordings from GET /recordings, supporting:
 * - Server-side filters: fromDate, toDate, agentId (sent in the query string)
 * - Lazy pagination: loadNextPage() appends the next 50-row page
 * - Client-side filters: queueId, durationMinSec/Max, hasTranscript (applied
 *   over `recordings` in-memory; results exposed as `filtered`)
 *
 * Uses a mounted-ref guard (codebase standard) so that a fetch resolving
 * after unmount never calls setState.
 */
export function useRecordingsList(filters: RecordingFilters): {
  recordings: Recording[]
  filtered: Recording[]
  loading: boolean
  error: string | null
  loadNextPage: () => Promise<void>
  refetch: () => Promise<void>
  hasMore: boolean
} {
  const [pages, setPages] = useState<Recording[][]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(true)

  // Mounted guard: prevents setState calls after unmount.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Build the query-string for a given page offset. Only server-side filters
  // (fromDate, toDate, agentId) are encoded here.
  const buildQS = useCallback(
    (offset: number): string => {
      const params = new URLSearchParams()
      // `fromDate` is an inclusive range START → midnight-local is correct.
      if (filters.fromDate) params.set('fromDate', filters.fromDate.toISOString())
      // `toDate` is an inclusive range END. The Calendar emits a midnight-local
      // Date, but the backend compares `created_at <= toDate`, so a raw
      // midnight `toDate` would exclude every recording made later that same
      // day. Snap it to the END of that local day (23:59:59.999) so the range
      // is genuinely inclusive of same-day recordings.
      if (filters.toDate) {
        const d = filters.toDate
        const endOfDay = new Date(
          d.getFullYear(),
          d.getMonth(),
          d.getDate(),
          23,
          59,
          59,
          999,
        )
        params.set('toDate', endOfDay.toISOString())
      }
      if (filters.agentId) params.set('agentId', filters.agentId)
      params.set('limit', '50')
      params.set('offset', String(offset))
      return params.toString()
    },
    [filters.fromDate, filters.toDate, filters.agentId],
  )

  const fetchPage = useCallback(
    async (offset: number): Promise<Recording[]> => {
      const resp = await api.get<{ recordings: Recording[]; limit: number; offset: number }>(
        `/recordings?${buildQS(offset)}`,
      )
      return resp.recordings
    },
    [buildQS],
  )

  /** Resets to page 0 and fetches the first page. Called automatically when
   *  server-affecting filters change (via the useEffect below). */
  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(null)
    setHasMore(true)
    try {
      const first = await fetchPage(0)
      if (mountedRef.current) {
        setPages([first])
        setHasMore(first.length === 50)
      }
    } catch (e) {
      if (mountedRef.current) {
        setError((e as Error).message)
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false)
      }
    }
  }, [fetchPage])

  // Re-fetch whenever the server-side filters change (refetch is memoised on
  // fetchPage → buildQS → fromDate/toDate/agentId).
  useEffect(() => {
    void refetch()
  }, [refetch])

  /** Appends the next page. No-ops when there is nothing more to load or a
   *  fetch is already in-flight. */
  const loadNextPage = useCallback(async (): Promise<void> => {
    if (!hasMore || loading) return
    setLoading(true)
    try {
      // Compute the current total row count across all accumulated pages.
      const offset = pages.reduce((acc, p) => acc + p.length, 0)
      const next = await fetchPage(offset)
      if (mountedRef.current) {
        setPages((prev) => [...prev, next])
        setHasMore(next.length === 50)
      }
    } catch (e) {
      if (mountedRef.current) {
        setError((e as Error).message)
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false)
      }
    }
  }, [fetchPage, hasMore, loading, pages])

  /** All rows from every fetched page, flattened. */
  const recordings = useMemo(() => pages.flat(), [pages])

  /** Subset of `recordings` that passes the client-side filter predicates. */
  const filtered = useMemo(
    () =>
      recordings.filter((r) => {
        if (filters.queueId && r.queueId !== filters.queueId) return false
        if (filters.durationMinSec !== null && r.durationSec < filters.durationMinSec) return false
        if (filters.durationMaxSec !== null && r.durationSec > filters.durationMaxSec) return false
        if (
          filters.hasTranscript !== null &&
          (r.hasTranscript ?? false) !== filters.hasTranscript
        )
          return false
        return true
      }),
    [recordings, filters.queueId, filters.durationMinSec, filters.durationMaxSec, filters.hasTranscript],
  )

  return { recordings, filtered, loading, error, loadNextPage, refetch, hasMore }
}
